<i18n lang="yaml" locale="en">
  items top: top
  items center: centered vertically
  items bottom: bottom
  flip: Flip
  background color front: Choose background color (front)
  text color front: Choose text color (front)
  alignment front: Align content (front)
  padding: Padding
  background color back: Choose background color (back)
  text color back: Choose text color (back)
  alignment back: Align content (back)
  y: Y
  x: X
  font (front): Font (front)
  font (back): Font (back)
  add font long: Add a font by dropping it here.
  add font short: Add as font.
</i18n>

<i18n lang="yaml" locale="de">
  items top: oben
  items center: vertikal zentriert
  items bottom: unten
  flip: Flip
  background color front: Hintergrundfarbe wählen (vorne)
  text color front: Textfarbe wählen (vorne)
  alignment front: Inhalt ausrichten (vorne)
  padding: Innenabstand
  background color back: Hintergrundfarbe wählen (hinten)
  text color back: Textfarbe wählen (hinten)
  alignment back: Innenabstand
  y: Y
  x: X
  font (front): Schriftart (Vorne)
  font (back): Schriftart (Hinten)
  add font long: Fügen Sie eine Schriftart hinzu, indem Sie sie hier ablegen.
  add font short: Als Schriftart hinzufügen.
</i18n>

<template>
  <div
    :class="boxClasses"
    :style="positionStyle"
    @click.capture="flipIf(!$editMode, $event)"
  >
    <transition
      :name="'flip' + flip"
    >
      <template
        v-if="activeChildren.length > 0"
      >
        <keep-alive
          v-for="child in activeChildren"
          :key="child.getters.path"
        >
          <!-- key is needed on the component for transition to work -->
          <component
            :key="child.getters.path"
            :path="child.getters.path"
            :is="child.getters.type"
            v-bind="child.getters.props"
            :class="contentClasses"
            :style="contentStyle"
          />
        </keep-alive>
      </template>
      <div
        v-else
        :key="activeFace"
        :class="contentClasses"
        :style="contentStyle"
      />
    </transition>
    <box-editor
      v-if="$editMode && gridActive"
      :_slot="activeFace"
      @flip="flipIf(true)"
      ref="editor"
    />
  </div>
</template>

<script>
import Base from './Base'
import FlipBoxEditor from '@/components/editor/FlipBoxEditor'
import colors from '@/defaultColors'
import fontHandler from '@/mixins/fontHandler'
import translatable from '@/store/mixins/translatable'

export default {
  // eslint-disable-next-line vue/name-property-casing
  name: 'flipBox',
  extends: Base,
  customStore: translatable,
  data () {
    return {
      flipped: false
    }
  },
  components: {
    boxEditor: FlipBoxEditor
  },
  mixins: [fontHandler([
    'fontFront',
    'fontBack'
  ])],
  props: {
    // eslint-disable-next-line
    _headlineFlip: {
      input: 'heading',
      label: 'flip'
    },
    flip: {
      type: String,
      default: 'y',
      label: 'flip',
      input: 'radio',
      choices: {
        y: 'y',
        x: 'x'
      },
      validator (value) {
        return ['y', 'x'].includes(value)
      }
    },
    /* front props */
    // eslint-disable-next-line
    _headlineBackgroundFront: {
      input: 'heading',
      label: 'background color front'
    },
    backgroundColorFront: {
      type: String,
      label: 'background color front',
      input: 'color',
      default: colors.colorMain
    },
    // eslint-disable-next-line
    _headlineTextColorFront: {
      input: 'heading',
      label: 'text color front'
    },
    textColorFront: {
      type: String,
      input: 'color',
      default: '#fff'
    },
    // eslint-disable-next-line
    _headlineAlignmentFront: {
      input: 'heading',
      label: 'alignment front'
    },
    itemAlignmentFront: {
      type: String,
      input: 'radio',
      choices: {
        'items-top': 'items top',
        'items-center': 'items center',
        'items-bottom': 'items bottom'
      },
      default: 'items-top',
      validator (value) {
        return ['items-top', 'items-center', 'items-bottom'].includes(value)
      }
    },
    paddingFront: {
      label: 'padding',
      input: 'checkbox',
      type: Boolean,
      default: false
    },
    /* back props */
    // eslint-disable-next-line
    _headlineBackgroundBack: {
      input: 'heading',
      label: 'background color back'
    },
    // input type radio color will be removed, so we don't change here
    backgroundColorBack: {
      type: String,
      label: 'background color back',
      input: 'color',
      default: colors.colorMainDarker
    },
    // eslint-disable-next-line
    _headlineTextColorBack: {
      input: 'heading',
      label: 'text color back'
    },
    // input type radio color will be removed, so we don't change here
    textColorBack: {
      type: String,
      input: 'color',
      default: '#fff'
    },
    // eslint-disable-next-line
    _headlineAlignmentBack: {
      input: 'heading',
      label: 'alignment back'
    },
    itemAlignmentBack: {
      type: String,
      input: 'radio',
      choices: {
        'items-top': 'items top',
        'items-center': 'items center',
        'items-bottom': 'items bottom'
      },
      default: 'items-top',
      validator (value) {
        return ['items-top', 'items-center', 'items-bottom'].includes(value)
      }
    },
    paddingBack: {
      label: 'padding',
      input: 'checkbox',
      type: Boolean,
      default: false
    },
    // eslint-disable-next-line
    _headlineFontFront: {
      input: 'heading',
      label: 'font (front)'
    },
    fontFront: {
      input: 'drop',
      type: String,
      contentType: 'font',
      placeholder: 'add font long',
      tooltip: 'add font short',
      default: ''
    },
    // eslint-disable-next-line
    _headlineFontBack: {
      input: 'heading',
      label: 'font (back)'
    },
    fontBack: {
      input: 'drop',
      type: String,
      contentType: 'font',
      placeholder: 'add font long',
      tooltip: 'add font short',
      default: ''
    }
  },
  computed: {
    boxClasses () {
      return {
        box: true,
        'edit-mode': this.$editMode
      }
    },
    classFront () {
      return {
        front: true,
        content: true,
        [this.itemAlignmentFront]: true,
        padding: this.paddingFront,
        [this.$project.format]: true
      }
    },
    classBack () {
      return {
        back: true,
        content: true,
        [this.itemAlignmentBack]: true,
        padding: this.paddingBack,
        [this.$project.format]: true
      }
    },
    styleFront () {
      return {
        'background-color': this.backgroundColorFront,
        color: this.textColorFront,
        ...this.fontStyles.fontFront
      }
    },
    styleBack () {
      return {
        'background-color': this.backgroundColorBack,
        color: this.textColorBack,
        ...this.fontStyles.fontBack
      }
    },
    contentClasses () {
      return this.flipped ? this.classBack : this.classFront
    },
    contentStyle () {
      return this.flipped ? this.styleBack : this.styleFront
    },
    activeFace () {
      return this.flipped ? 'back' : 'front'
    },
    activeChildren () {
      return this.children.filter(child => child.getters.slot === this.activeFace)
    }
  },
  methods: {
    flipIf (condition, event) {
      if (condition) {
        this.flipped = !this.flipped
        if (event) {
          event.stopPropagation()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import './box';

/* box styles */
.box {
  .content::after {
    content: url('~@/assets/icons/tw-icon-flip-white.svg');
    width: 24px;
    height: 24px;
    position: absolute;
    top: 14px;
    right: 14px;
  }

  &.edit-mode {
    box-shadow: 0 0 1px #ff0080;
  }
}
</style>
